/**
 * DocumentsTab (Tab 5) — Broker document library: upload & manage source files.
 *
 * This is the INPUT VALVE for the RAG pipeline:
 *   1. Broker uploads PDFs here (drag-drop, categorized)
 *   2. Backend AI auto-analyzes and produces structured metadata
 *   3. Broker enriches that analysis in the AI Review tab (Tab 6)
 *   4. Broker Chat Bot retrieves enriched metadata at runtime
 *
 * Matches the original components/BrokerIntelligence/BrokerDocumentManager.tsx
 * in navy+gold. Same backend endpoints, same document store as AI Review.
 *
 * Backend:
 *   POST   /documents/upload (multipart FormData)
 *   GET    /documents/my-documents?owner_type=brokerage&brokerage_id=...
 *   DELETE /documents/:id
 *   POST   /broker-docmaster/analyze/:brokerageId (trigger AI analysis)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolderOpen,
  faFileAlt,
  faRocket,
  faLightbulb,
  faCheckCircle,
  faUpload,
  faTrash,
  faRobot,
  faCalendarAlt,
  faCloudArrowUp,
  faEye,
  faBrain,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import axiosInstance from '../../../helpers/api';
import {
  SplitPanel,
  SearchBar,
  FilterPills,
  ListItem,
  ProfileHeader,
  InlineForm,
  NavySelect,
  GoldButton,
  OutlineButton,
  colors,
  fonts,
} from '../shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Document {
  id: string;
  user_id?: string;
  filename: string;
  original_filename?: string;
  document_type: string;
  file_size?: number;
  file_size_formatted?: string;
  upload_date?: string;
  created_at?: string;
  updated_at?: string;
  processed_at?: string;
  status?: string;
  processing_status?: string;
  ai_analysis?: any;
  s3_url?: string;
  s3_key?: string;
  visibility?: string;
  owner_type?: string;
  brokerage_id?: string;
  description?: string;
}

interface DocumentsTabProps {
  brokerageId?: string;
  brokerageName?: string;
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

interface CategoryDef {
  key: string;
  label: string;
  icon: IconDefinition;
  color: string;
}

const CATEGORIES: CategoryDef[] = [
  { key: 'all',                label: 'All',          icon: faFolderOpen,   color: colors.blue },
  { key: 'general',            label: 'General',      icon: faFolderOpen,   color: colors.muted },
  { key: 'contract_template',  label: 'Contracts',    icon: faFileAlt,      color: colors.gold2 },
  { key: 'marketing_material', label: 'Marketing',    icon: faRocket,       color: colors.gold },
  { key: 'training_resource',  label: 'Training',     icon: faLightbulb,    color: colors.green },
  { key: 'legal_document',     label: 'Legal',        icon: faFileAlt,      color: colors.orange },
  { key: 'form_template',      label: 'Forms',        icon: faCheckCircle,  color: colors.red },
  { key: 'disclosure',         label: 'Disclosures',  icon: faFileAlt,      color: colors.blue },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DocumentsTab: React.FC<DocumentsTabProps> = ({ brokerageId, brokerageName }) => {
  const effectiveBrokerageId = brokerageId || localStorage.getItem('brokerageId') || '';

  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selected, setSelected] = useState<Document | null>(null);

  // Upload state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Default to 'general' so the backend's enrichment service classifies
  // the content from the actual PDF text instead of being steered by a
  // pre-declared "contract_template" tag. Old default caused AI
  // Disclosures, Marketing flyers, etc. to be summarized as if they
  // were purchase contracts because the enrichment prompt honored the
  // document_type hint without reading the content first.
  const [uploadCategory, setUploadCategory] = useState('general');
  const [dragActive, setDragActive] = useState(false);

  // UX: uploads inherit the ACTIVE category tab (users expect "I'm on
  // Marketing, so my upload is Marketing"). The dropdown still overrides.
  useEffect(() => {
    if (categoryFilter !== 'all') setUploadCategory(categoryFilter);
  }, [categoryFilter]);
  const [actionAlert, setActionAlert] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---- Fetch ---------------------------------------------------------------
  const fetchDocuments = useCallback(async () => {
    if (!effectiveBrokerageId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/documents/my-documents', {
        params: { owner_type: 'brokerage', brokerage_id: effectiveBrokerageId },
      });
      if (res.data?.success) {
        setDocuments(res.data.documents || []);
      } else {
        setError(res.data?.error || 'Failed to load documents');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [effectiveBrokerageId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // ---- Derived -------------------------------------------------------------
  const filtered = useMemo(() => {
    return documents.filter((d) => {
      if (search.trim()) {
        const q = search.toLowerCase();
        const hit =
          d.filename?.toLowerCase().includes(q) ||
          d.original_filename?.toLowerCase().includes(q) ||
          d.document_type?.toLowerCase().includes(q) ||
          d.description?.toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (categoryFilter !== 'all' && d.document_type !== categoryFilter) return false;
      return true;
    });
  }, [documents, search, categoryFilter]);

  const stats = useMemo(() => {
    const total = documents.length;
    const analyzed = documents.filter((d) => d.ai_analysis).length;
    const processed = documents.filter((d) => d.processing_status === 'success').length;
    const totalSize = documents.reduce((sum, d) => sum + (d.file_size || 0), 0);
    return {
      total,
      analyzed,
      pending: total - analyzed,
      totalSizeMb: Math.round(totalSize / 1024 / 1024),
    };
  }, [documents]);

  // ---- Upload --------------------------------------------------------------
  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      if (!effectiveBrokerageId) {
        setActionAlert('❌ Brokerage not ready — refresh and try again');
        setTimeout(() => setActionAlert(null), 4000);
        return;
      }
      setUploading(true);
      const arr = Array.from(files);
      let successes = 0;
      let failures = 0;

      for (const file of arr) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', uploadCategory);
        formData.append('description', `${brokerageName || 'Brokerage'} shared document`);
        formData.append('owner_type', 'brokerage');
        formData.append('visibility', 'brokerage_wide');
        formData.append('brokerage_id', effectiveBrokerageId);
        try {
          const res = await axiosInstance.post('/documents/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
          });
          if (res.data?.success) successes++;
          else failures++;
        } catch {
          failures++;
        }
      }

      setUploading(false);
      setShowUploadForm(false);
      setActionAlert(
        failures === 0
          ? `✅ Uploaded ${successes} file${successes === 1 ? '' : 's'}`
          : `⚠️ ${successes} uploaded, ${failures} failed`
      );
      setTimeout(() => setActionAlert(null), 4000);
      await fetchDocuments();
    },
    [effectiveBrokerageId, brokerageName, uploadCategory, fetchDocuments]
  );

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleDelete = async (doc: Document) => {
    if (!window.confirm(`Delete "${doc.original_filename || doc.filename}"? This cannot be undone.`)) {
      return;
    }
    try {
      const res = await axiosInstance.delete(`/documents/${doc.id}`);
      if (res.data?.success) {
        setActionAlert('✅ Document deleted');
        if (selected?.id === doc.id) setSelected(null);
        await fetchDocuments();
      } else {
        setActionAlert(`❌ ${res.data?.error || 'Delete failed'}`);
      }
    } catch (err: any) {
      setActionAlert(`❌ ${err?.response?.data?.error || err?.message || 'Delete failed'}`);
    } finally {
      setTimeout(() => setActionAlert(null), 4000);
    }
  };

  const handleAnalyze = async (doc: Document) => {
    if (!effectiveBrokerageId) return;
    setAnalyzing(true);
    try {
      const res = await axiosInstance.post(`/broker-docmaster/analyze/${effectiveBrokerageId}`, {
        document_id: doc.id,
      });
      if (res.data?.success !== false) {
        setActionAlert('✅ AI analysis triggered — check back in a moment');
        await fetchDocuments();
      } else {
        setActionAlert(`❌ ${res.data?.error || 'Analyze failed'}`);
      }
    } catch (err: any) {
      setActionAlert(`❌ ${err?.response?.data?.error || err?.message || 'Analyze failed'}`);
    } finally {
      setAnalyzing(false);
      setTimeout(() => setActionAlert(null), 4000);
    }
  };

  // ---- Render --------------------------------------------------------------
  return (
    <>
      <StatsBar stats={stats} loading={loading} />

      {actionAlert && (
        <div
          style={{
            background: actionAlert.startsWith('✅') ? '#d4edda' : actionAlert.startsWith('⚠️') ? '#fff3cd' : '#f8d7da',
            color: actionAlert.startsWith('✅') ? colors.green : actionAlert.startsWith('⚠️') ? colors.orange : colors.red,
            padding: '8px 24px',
            borderBottom: `1px solid ${colors.border}`,
            fontSize: '13px',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {actionAlert}
        </div>
      )}

      <SplitPanel
        left={
          <>
            <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                <SearchBar value={search} onChange={setSearch} placeholder="Search documents..." />
                <GoldButton
                  onClick={() => setShowUploadForm((v) => !v)}
                  style={{ whiteSpace: 'nowrap', padding: '8px 12px' }}
                >
                  {showUploadForm ? '✕' : '+ Upload'}
                </GoldButton>
              </div>

              {showUploadForm && (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                  style={{
                    background: dragActive ? `${colors.gold}22` : colors.navy3,
                    border: `2px dashed ${dragActive ? colors.gold : colors.gold}55`,
                    borderRadius: '8px',
                    padding: '16px 12px',
                    marginBottom: '8px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FontAwesomeIcon
                    icon={faCloudArrowUp}
                    style={{ color: colors.gold, fontSize: '22px', marginBottom: '6px' }}
                  />
                  <div style={{ fontSize: '12px', fontWeight: 600, color: colors.white, marginBottom: '4px' }}>
                    {uploading ? 'Uploading…' : 'Drop files or click to browse'}
                  </div>
                  <div style={{ fontSize: '10px', color: colors.muted, marginBottom: '8px' }}>
                    PDF, DOCX, images accepted
                  </div>
                  <NavySelect
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ fontSize: '11px' }}
                  >
                    {CATEGORIES.filter((c) => c.key !== 'all').map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                      </option>
                    ))}
                  </NavySelect>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files) handleFiles(e.target.files);
                    }}
                  />
                </div>
              )}

              <FilterPills
                options={CATEGORIES.map((c) => ({ key: c.key, label: c.label }))}
                activeKey={categoryFilter}
                onChange={setCategoryFilter}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: '32px', textAlign: 'center', color: colors.muted }}>
                  Loading documents…
                </div>
              ) : error ? (
                <div style={{ padding: '20px', color: colors.red, fontSize: '12px' }}>{error}</div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
                  <FontAwesomeIcon icon={faFolderOpen} style={{ fontSize: '32px', marginBottom: '8px' }} />
                  <div style={{ fontSize: '13px' }}>
                    {documents.length === 0
                      ? 'No documents yet. Upload your first broker document!'
                      : 'No documents match current filters'}
                  </div>
                </div>
              ) : (
                filtered.map((d) => (
                  <ListItem
                    key={d.id}
                    selected={selected?.id === d.id}
                    onClick={() => setSelected(d)}
                    title={d.original_filename || d.filename || '(unnamed)'}
                    subtitle={categoryLabel(d.document_type)}
                    meta={
                      <span>
                        {d.file_size_formatted || formatBytes(d.file_size)} ·{' '}
                        {formatRelativeTime(d.upload_date || d.created_at)}
                      </span>
                    }
                    rightContent={
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                        <CategoryBadge type={d.document_type} />
                        {d.ai_analysis ? (
                          <span
                            style={{
                              background: `${colors.green}22`,
                              color: colors.green,
                              fontSize: '9px',
                              fontWeight: 700,
                              padding: '1px 6px',
                              borderRadius: '4px',
                              textTransform: 'uppercase',
                            }}
                          >
                            <FontAwesomeIcon icon={faBrain} /> AI
                          </span>
                        ) : null}
                      </div>
                    }
                  />
                ))
              )}
            </div>
          </>
        }
        right={
          selected ? (
            <DocumentDetail
              document={selected}
              analyzing={analyzing}
              onAnalyze={() => handleAnalyze(selected)}
              onDelete={() => handleDelete(selected)}
            />
          ) : (
            <EmptyDetailState />
          )
        }
      />
    </>
  );
};

// ===========================================================================
// Stats bar
// ===========================================================================

const StatsBar: React.FC<{ stats: { total: number; analyzed: number; pending: number; totalSizeMb: number }; loading: boolean }> = ({ stats, loading }) => (
  <div
    style={{
      background: colors.offBg,
      borderBottom: `1px solid ${colors.border}`,
      padding: '16px 24px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '12px',
      flexShrink: 0,
    }}
  >
    <StatCard icon={faFileAlt}     label="Total Documents" value={loading ? '—' : stats.total}      color={colors.blue} />
    <StatCard icon={faBrain}       label="AI Analyzed"     value={loading ? '—' : stats.analyzed}   color={colors.green} />
    <StatCard icon={faRobot}       label="Pending Analysis" value={loading ? '—' : stats.pending}   color={colors.orange} />
    <StatCard icon={faCloudArrowUp} label="Storage (MB)"   value={loading ? '—' : stats.totalSizeMb} color={colors.gold2} />
  </div>
);

const StatCard: React.FC<{ icon: any; label: string; value: React.ReactNode; color: string }> = ({ icon, label, value, color }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${color}`,
      borderRadius: '8px',
      padding: '14px 18px',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
    }}
  >
    <div
      style={{
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        background: `${color}22`,
        border: `2px solid ${color}55`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ color, fontSize: '14px' }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: fonts.display, fontSize: '24px', color: colors.navy, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '11px', fontWeight: 600, color: colors.navy, marginTop: '2px' }}>{label}</div>
    </div>
  </div>
);

// ===========================================================================
// Right panel — document detail
// ===========================================================================

const DocumentDetail: React.FC<{
  document: Document;
  analyzing: boolean;
  onAnalyze: () => void;
  onDelete: () => void;
}> = ({ document: doc, analyzing, onAnalyze, onDelete }) => {
  const hasAnalysis = !!doc.ai_analysis;

  return (
    <div>
      <ProfileHeader
        title={doc.original_filename || doc.filename || '(unnamed)'}
        metaItems={[
          { icon: faFileAlt, text: categoryLabel(doc.document_type) },
          { icon: faCalendarAlt, text: `Uploaded ${formatRelativeTime(doc.upload_date || doc.created_at)}` },
          ...(doc.file_size_formatted || doc.file_size
            ? [{ text: doc.file_size_formatted || formatBytes(doc.file_size) }]
            : []),
        ]}
        badges={<CategoryBadge type={doc.document_type} />}
        rightContent={
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px', justifyContent: 'flex-end' }}>
            {doc.s3_url && (
              <a
                href={doc.s3_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'transparent',
                  border: `1px solid ${colors.muted}`,
                  color: colors.muted,
                  padding: '8px 14px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 500,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                <FontAwesomeIcon icon={faEye} /> Preview
              </a>
            )}
            <GoldButton onClick={onAnalyze} disabled={analyzing}>
              <FontAwesomeIcon icon={faBrain} /> {analyzing ? 'Analyzing…' : hasAnalysis ? 'Re-analyze' : 'Analyze with AI'}
            </GoldButton>
            <OutlineButton
              onClick={onDelete}
              style={{ borderColor: colors.red, color: colors.red }}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete
            </OutlineButton>
          </div>
        }
      />

      <div style={{ padding: '0 24px 24px 24px' }}>
        {/* Analysis status banner */}
        {hasAnalysis ? (
          <div
            style={{
              background: `${colors.green}11`,
              border: `1px solid ${colors.green}55`,
              borderLeft: `4px solid ${colors.green}`,
              borderRadius: '8px',
              padding: '14px 18px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green, fontSize: '20px' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: colors.navy }}>
                AI Analysis Complete
              </div>
              <div style={{ fontSize: '12px', color: colors.muted }}>
                This document has structured metadata. Enrich it further in the{' '}
                <strong>AI Review</strong> tab to feed the broker chatbot.
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              background: `${colors.orange}11`,
              border: `1px solid ${colors.orange}55`,
              borderLeft: `4px solid ${colors.orange}`,
              borderRadius: '8px',
              padding: '14px 18px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <FontAwesomeIcon icon={faRobot} style={{ color: colors.orange, fontSize: '20px' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: colors.navy }}>
                Awaiting AI Analysis
              </div>
              <div style={{ fontSize: '12px', color: colors.muted }}>
                Click "Analyze with AI" to extract structured metadata for the RAG chatbot.
              </div>
            </div>
          </div>
        )}

        <SectionCard title="Document Details">
          <KeyValue k="Filename" v={doc.original_filename || doc.filename || '—'} />
          <KeyValue k="Type" v={categoryLabel(doc.document_type)} />
          <KeyValue k="Size" v={doc.file_size_formatted || formatBytes(doc.file_size) || '—'} />
          <KeyValue k="Uploaded" v={formatAbsoluteTime(doc.upload_date || doc.created_at)} />
          {doc.processed_at && <KeyValue k="Processed" v={formatAbsoluteTime(doc.processed_at)} />}
          <KeyValue
            k="Status"
            v={
              <span
                style={{
                  background: `${colors.green}22`,
                  color: colors.green,
                  fontSize: '10px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                }}
              >
                {doc.processing_status || 'uploaded'}
              </span>
            }
          />
        </SectionCard>

        {doc.description && (
          <SectionCard title="Description">
            <div style={{ fontSize: '13px', color: colors.navy, whiteSpace: 'pre-wrap' }}>
              {doc.description}
            </div>
          </SectionCard>
        )}

        {hasAnalysis && (
          <SectionCard title="Quick AI Summary">
            <div style={{ fontSize: '13px', color: colors.navy, lineHeight: 1.6 }}>
              {(() => {
                const a = typeof doc.ai_analysis === 'string' ? safeParse(doc.ai_analysis) : doc.ai_analysis;
                return a?.summary || a?.analysis_details?.summary || 'No summary available';
              })()}
            </div>
            <div style={{ fontSize: '11px', color: colors.muted, marginTop: '10px' }}>
              Full editor lives in the <strong>AI Review</strong> tab — Uploaded Documents sub-tab.
            </div>
          </SectionCard>
        )}
      </div>
    </div>
  );
};

// ===========================================================================
// Helpers
// ===========================================================================

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '8px',
      padding: '16px 20px',
      marginBottom: '12px',
    }}
  >
    <div
      style={{
        fontSize: '11px',
        fontWeight: 700,
        color: colors.navy,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '10px',
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const KeyValue: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '6px 0',
      borderBottom: `1px solid ${colors.border}`,
      fontSize: '13px',
    }}
  >
    <span style={{ color: colors.muted }}>{k}</span>
    <span style={{ color: colors.navy, fontWeight: 500, textAlign: 'right' }}>{v}</span>
  </div>
);

const CategoryBadge: React.FC<{ type?: string }> = ({ type }) => {
  const def = CATEGORIES.find((c) => c.key === type) || CATEGORIES[0];
  return (
    <span
      style={{
        background: `${def.color}22`,
        color: def.color,
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {def.label}
    </span>
  );
};

function categoryLabel(type?: string): string {
  return CATEGORIES.find((c) => c.key === type)?.label || type || 'Document';
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function formatAbsoluteTime(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
}

function safeParse(s: string): any {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

const EmptyDetailState: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '48px',
      color: colors.muted,
    }}
  >
    <FontAwesomeIcon icon={faFolderOpen} style={{ fontSize: '48px', marginBottom: '12px', color: colors.gold3 }} />
    <div style={{ fontSize: '15px', fontWeight: 600, color: colors.navy }}>Select a document</div>
    <div style={{ fontSize: '12px', marginTop: '4px', textAlign: 'center' }}>
      Choose a document from the library to preview,<br />
      trigger AI analysis, or delete
    </div>
  </div>
);

export default DocumentsTab;
